import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Chart, ChartConfiguration, ChartData, ChartDataset } from 'chart.js';
import type {} from 'chartjs-plugin-dragdata';
import zoomPlugin from 'chartjs-plugin-zoom';
import { BaseChartDirective } from 'ng2-charts';
import { TPASensor, TPASensorReading, TPAStage, TPATestData } from '../models/tpa-test-data.modal';
import { SampleInformationDialogComponent, SampleInformationDialogData } from '../sample-information-dialog/sample-information-dialog.component';
import { TemperaturesDialogComponent, TemperaturesDialogData } from '../temperatures-dialog/temperatures-dialog.component';
import { TestInformationDialogComponent, TestInformationDialogData } from '../test-information-dialog/test-information-dialog.component';
import { TpaService } from '../services/tpa.service';
import { MIN_FIT_SAMPLES, StageAnalysis, analyseStage, analyseTimeRange } from '../utils/stage-analysis.utils';

Chart.register(zoomPlugin);

type ChartPoint = { x: number; y: number };

export interface TimeRange {
  startTime: number;
  endTime: number;
}

export interface SensorFigure {
  sensorNumber: number;
  title: string;
  readings: TPASensorReading[];
  data: ChartData<'line', ChartPoint[]>;
  options: ChartConfiguration<'line'>['options'];
}

@Component({
    selector: 'app-preview-test-data',
    templateUrl: './preview-test-data.component.html',
    styleUrls: ['./preview-test-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PreviewTestDataComponent implements OnInit, OnDestroy {
  private static readonly SERIES_COLOR = '#008300';

  private static readonly FIT_COLOR = '#2a78d6';

  private static readonly CUSTOM_FIT_COLOR = '#e34948';

  private static readonly NON_INTEGER_KEYS = ['.', ',', 'e', 'E', '+', '-'];

  readonly minFitSamples = MIN_FIT_SAMPLES;

  projectId = '';

  fileName = '';

  testData: TPATestData | null = null;

  sensorFigures: SensorFigure[] = [];

  selectedSensorNumber: number | null = null;

  stages: TPAStage[] = [];

  selectedStageNumber: number | null = null;

  stageAnalysis: StageAnalysis | null = null;

  customStartTime: number | null = null;

  customEndTime: number | null = null;

  appliedCustomRange: TimeRange | null = null;

  customAnalysis: StageAnalysis | null = null;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  private resizeObserver?: ResizeObserver;

  private fillFrameId?: number;

  constructor(
    private route: ActivatedRoute,
    private tpaService: TpaService,
    private elementRef: ElementRef<HTMLElement>,
    private dialog: MatDialog,
  ) {}

  get selectedSensorFigure(): SensorFigure | undefined {
    return this.sensorFigures.find(
      (figure) => figure.sensorNumber === this.selectedSensorNumber,
    );
  }

  get selectedStage(): TPAStage | undefined {
    return this.stages.find((stage) => stage.stageNumber === this.selectedStageNumber);
  }

  get canUpdateCustomTr(): boolean {
    return Number.isInteger(this.customStartTime) && Number.isInteger(this.customEndTime);
  }

  ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.scheduleFillAvailableHeight());
    this.resizeObserver.observe(document.body);
    this.route.queryParamMap.subscribe((params) => {
      this.projectId = params.get('projectId') || '';
      this.fileName = params.get('fileName') || '';
      this.getTestData();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.fillFrameId !== undefined) cancelAnimationFrame(this.fillFrameId);
  }

  @HostListener('window:resize')
  scheduleFillAvailableHeight(): void {
    if (this.fillFrameId !== undefined) cancelAnimationFrame(this.fillFrameId);
    this.fillFrameId = requestAnimationFrame(() => {
      this.fillFrameId = undefined;
      const host = this.elementRef.nativeElement;
      const top = host.getBoundingClientRect().top + window.scrollY;
      host.style.height = `${Math.max(Math.floor(window.innerHeight - top), 0)}px`;
    });
  }

  getTestData(): void {
    if (!this.projectId || !this.fileName) return;
    this.tpaService.viewTestData(this.projectId, this.fileName).subscribe({
      next: (testData) => {
        this.testData = testData;
        this.sensorFigures = this.buildSensorFigures(testData);
        this.selectedSensorNumber = this.sensorFigures[0]?.sensorNumber ?? null;
        this.stages = testData.stages ?? [];
        this.selectedStageNumber = 0;
        this.resetCustomTr();
        this.updateFits();
      },
      error: (error: HttpErrorResponse) => {
        this.testData = error.status === 404 ? error.error : null;
        this.sensorFigures = [];
        this.selectedSensorNumber = null;
        this.stages = [];
        this.selectedStageNumber = null;
        this.stageAnalysis = null;
        this.resetCustomTr();
      },
    });
  }

  selectSensor(sensorNumber: number): void {
    this.selectedSensorNumber = sensorNumber;
    this.updateFits();
  }

  selectStage(stageNumber: number): void {
    this.selectedStageNumber = stageNumber;
    this.updateFits();
  }

  updateCustomTr(): void {
    if (!this.canUpdateCustomTr) return;
    this.appliedCustomRange = {
      startTime: Number(this.customStartTime),
      endTime: Number(this.customEndTime),
    };
    this.updateFits();
  }

  openSampleInformation(): void {
    const stage = this.selectedStage;
    const data: SampleInformationDialogData = {
      stageNumber: stage?.stageNumber ?? null,
      probeLabels: (this.testData?.probeLabels ?? []).map((label) => ({
        ...label,
        tr: this.getSensorTr(label.sensorNumber, stage),
      })),
    };
    this.dialog.open(SampleInformationDialogComponent, {
      panelClass: 'app-dialog',
      width: '960px',
      maxWidth: '95vw',
      data,
    });
  }

  openTestInformation(): void {
    const data: TestInformationDialogData = {
      noOfProbes: this.testData?.noOfProbes,
      linearResistance: this.testData?.linearResistance,
      effectiveLength: this.testData?.effectiveLength,
      effectiveRadius: this.testData?.effectiveRadius,
      stages: this.stages,
    };
    this.dialog.open(TestInformationDialogComponent, {
      panelClass: 'app-dialog',
      width: '760px',
      maxWidth: '95vw',
      data,
    });
  }

  openTemperatures(): void {
    const data: TemperaturesDialogData = {
      readings: this.testData?.readings ?? [],
    };
    this.dialog.open(TemperaturesDialogComponent, {
      panelClass: 'app-dialog',
      width: '960px',
      maxWidth: '95vw',
      data,
    });
  }

  blockNonIntegerKeys(event: KeyboardEvent): void {
    if (PreviewTestDataComponent.NON_INTEGER_KEYS.includes(event.key)) event.preventDefault();
  }

  formatValue(value: number, digits: number, unit?: string): string {
    if (!Number.isFinite(value)) return '—';
    return unit ? `${value.toFixed(digits)} ${unit}` : value.toFixed(digits);
  }

  formatFitRange(analysis: StageAnalysis): string {
    return `${this.formatValue(Math.exp(analysis.startX), 0)}–${this.formatValue(Math.exp(analysis.endX), 0)} s`;
  }

  resetZoom(): void {
    this.chart?.chart?.resetZoom();
  }

  trackBySensorNumber(_: number, figure: SensorFigure): number {
    return figure.sensorNumber;
  }

  private buildSensorFigures(testData: TPATestData): SensorFigure[] {
    return (testData.sensors ?? []).map((sensor) => {
      const points = sensor.readings
        .filter((reading) => reading.time > 0)
        .map((reading) => ({
          x: reading.time,
          y: reading.temperature,
        }));
      return {
        sensorNumber: sensor.sensorNumber,
        title: this.getSensorTitle(sensor, testData),
        readings: sensor.readings,
        data: {
          datasets: [
            {
              label: `Sensor ${sensor.sensorNumber}`,
              data: points,
              borderColor: PreviewTestDataComponent.SERIES_COLOR,
              backgroundColor: PreviewTestDataComponent.SERIES_COLOR,
              borderWidth: 2,
              pointStyle: 'circle',
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: PreviewTestDataComponent.SERIES_COLOR,
              pointBorderColor: '#fff',
              pointBorderWidth: 1,
              tension: 0,
            },
          ],
        },
        options: this.buildChartOptions(points.map((point) => point.y)),
      };
    });
  }

  private resetCustomTr(): void {
    this.customStartTime = null;
    this.customEndTime = null;
    this.appliedCustomRange = null;
    this.customAnalysis = null;
  }

  private updateFits(): void {
    const figure = this.selectedSensorFigure;
    const stage = this.selectedStage;
    const customRange = this.appliedCustomRange;
    this.stageAnalysis = figure && stage ? analyseStage(figure.readings, stage) : null;
    this.customAnalysis = figure && customRange
      ? analyseTimeRange(figure.readings, customRange.startTime, customRange.endTime)
      : null;
    if (!figure) return;

    const [readingsDataset] = figure.data.datasets;
    const datasets = [readingsDataset];
    if (stage && this.hasFit(this.stageAnalysis)) {
      datasets.push(this.buildFitDataset(
        figure.readings,
        this.stageAnalysis,
        `Fitted line (stage ${stage.stageNumber})`,
        PreviewTestDataComponent.FIT_COLOR,
        [6, 4],
      ));
    }
    if (customRange && this.hasFit(this.customAnalysis)) {
      datasets.push(this.buildFitDataset(
        figure.readings,
        this.customAnalysis,
        `Custom TR line (${customRange.startTime}–${customRange.endTime} s)`,
        PreviewTestDataComponent.CUSTOM_FIT_COLOR,
        [2, 3],
      ));
    }
    figure.data = {
      datasets,
    };
  }

  private getSensorTr(sensorNumber: number, stage: TPAStage | undefined): number {
    const figure = this.sensorFigures.find((candidate) => candidate.sensorNumber === sensorNumber);
    const analysis = figure && stage ? analyseStage(figure.readings, stage) : null;
    return this.hasFit(analysis) ? analysis.tr : NaN;
  }

  private hasFit(analysis: StageAnalysis | null): analysis is StageAnalysis {
    return !!analysis && analysis.sampleCount >= MIN_FIT_SAMPLES;
  }

  private buildFitDataset(
    readings: TPASensorReading[],
    analysis: StageAnalysis,
    label: string,
    color: string,
    borderDash: number[],
  ): ChartDataset<'line', ChartPoint[]> {
    return {
      label,
      data: readings
        .filter((reading) => reading.time > 0)
        .map((reading) => ({
          x: reading.time,
          y: analysis.slope * Math.log(reading.time) + analysis.intercept,
        })),
      borderColor: color,
      backgroundColor: color,
      borderWidth: 2,
      borderDash,
      pointRadius: 0,
      pointHoverRadius: 4,
      tension: 0,
    };
  }

  private buildChartOptions(temperatures: number[]): ChartConfiguration<'line'>['options'] {
    const hasTemperatures = temperatures.length > 0;
    const yMin = hasTemperatures ? Math.floor(Math.min(...temperatures) / 5) * 5 : undefined;
    const yMax = hasTemperatures ? Math.ceil(Math.max(...temperatures) / 5) * 5 : undefined;
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        dragData: false,
        legend: {
          display: true,
          position: 'top',
        },
        zoom: {
          limits: {
            x: {
              min: 'original',
              max: 'original',
            },
            y: {
              min: 'original',
              max: 'original',
            },
          },
          pan: {
            enabled: true,
            mode: 'xy',
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'xy',
          },
        },
        tooltip: {
          callbacks: {
            title: (items) => `Time: ${items[0].parsed.x} s`,
            label: (item) => `${item.dataset.label}: ${Number(item.parsed.y).toFixed(3)} °C`,
          },
        },
      },
      scales: {
        x: {
          type: 'logarithmic',
          title: {
            display: true,
            text: 'Time (s, log scale)',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.06)',
          },
        },
        y: {
          min: yMin,
          max: yMax,
          title: {
            display: true,
            text: 'Temperature (°C)',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.06)',
          },
        },
      },
    };
  }

  private getSensorTitle(sensor: TPASensor, testData: TPATestData): string {
    const probeLabel = testData.probeLabels?.find(
      (label) => label.sensorNumber === sensor.sensorNumber,
    );
    if (!probeLabel) return `Sensor ${sensor.sensorNumber}`;
    return `Sensor ${sensor.sensorNumber} — ${probeLabel.sampleId} (${probeLabel.inHouseId})`;
  }
}
